import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Gauge } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, SplitView } from '@vnetwork/ui/layout';
import {
  Button,
  Stepper,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useMonitorTypes,
  useCreateKumaMonitorRaw,
  type MonitorFieldSpec,
  type MonitorTypeSpec,
} from '@/api/monitoring/kuma';

const STEPS = ['Type', 'Configure', 'Review'];

/**
 * Add-monitor wizard. Step 1 picks a monitor type, step 2 renders that type's
 * fields (schema-driven from `GET /monitoring/monitor-types`), step 3 reviews
 * and creates. The raw object is POSTed — the backend fills required defaults.
 */
export function KumaMonitorCreate() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const listPath = `${base}/kuma-uptime`;
  const { show } = useToast();

  const { data: types = [] } = useMonitorTypes();
  const create = useCreateKumaMonitorRaw();

  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [typeKey, setTypeKey] = useState('');
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [showErrors, setShowErrors] = useState(false);

  const selected = useMemo<MonitorTypeSpec | undefined>(
    () => types.find((t) => t.type === typeKey),
    [types, typeKey],
  );
  const fields = selected?.fields ?? [];
  const missing = fields.filter((f) => f.required && String(values[f.key] ?? '').trim() === '');

  const goto = (i: number) => {
    setStep(i);
    setReached((r) => Math.max(r, i));
  };

  const pickType = (t: MonitorTypeSpec) => {
    setTypeKey(t.type);
    const init: Record<string, unknown> = {};
    for (const f of t.fields) if (f.default !== undefined) init[f.key] = f.default;
    setValues(init);
  };

  const set = (k: string, v: unknown) => setValues((prev) => ({ ...prev, [k]: v }));

  const next = () => {
    if (step === 0 && !selected) return;
    if (step === 1 && missing.length > 0) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    goto(step + 1);
  };

  const submit = () => {
    if (!selected || missing.length > 0) {
      setShowErrors(true);
      setStep(1);
      return;
    }
    const payload: Record<string, unknown> = { type: selected.type };
    for (const f of fields) {
      let v = values[f.key];
      if (v === undefined || v === '') continue;
      if (f.kind === 'number') v = Number(v);
      else if (f.kind === 'bool') v = v === true || v === 'true';
      if (Array.isArray(f.default) && !Array.isArray(v)) v = [v];
      payload[f.key] = v;
    }
    create.mutate(payload, {
      onSuccess: () => {
        const nm = String(values.name ?? '');
        show({ tone: 'success', title: `Monitor "${nm}" created` });
        navigate(nm ? `${listPath}/${encodeURIComponent(nm)}` : listPath);
      },
      onError: (e) => show({ tone: 'danger', title: e?.message || 'Create failed' }),
    });
  };

  const renderControl = (f: MonitorFieldSpec, fp: object) => {
    const val = values[f.key];
    if (f.kind === 'select') {
      return (
        <Select value={String(val ?? '')} onValueChange={(v) => set(f.key, v)}>
          <SelectTrigger {...fp}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {(f.options ?? []).filter((o) => o !== '').map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (f.kind === 'bool') {
      return (
        <Select value={String(val ?? false)} onValueChange={(v) => set(f.key, v === 'true')}>
          <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="false">No</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        {...fp}
        type={f.kind === 'number' ? 'number' : 'text'}
        value={val == null ? '' : String(val)}
        onChange={(e) => set(f.key, e.target.value)}
        placeholder={f.help}
        invalid={showErrors && f.required && String(val ?? '').trim() === ''}
      />
    );
  };

  const summary = (
    <Card>
      <CardHeader>
        <CardTitle>Summary</CardTitle>
        <CardDescription>Review before creating.</CardDescription>
      </CardHeader>
      <CardBody>
        <dl style={{ display: 'grid', gap: 'var(--vn-space-2)', margin: 0 }}>
          <Row label="Type" value={selected?.label ?? '—'} />
          <Row label="Name" value={String(values.name ?? '') || '—'} />
          <Row label="URL / Host" value={String(values.url ?? values.hostname ?? '') || '—'} />
          {values.keyword ? <Row label="Keyword" value={String(values.keyword)} /> : null}
          <Row label="Interval" value={`${values.interval ?? 60}s`} />
        </dl>
      </CardBody>
    </Card>
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Gauge size={20} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={listPath}>Uptime</Link></BreadcrumbItem>
            <BreadcrumbItem current>New monitor</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="New monitor"
        description="Create an Uptime-Kuma monitor to track an endpoint, port, host or signal."
      />

      <div style={{ marginBottom: 'var(--vn-space-5)' }}>
        <Stepper steps={STEPS} current={step} reached={reached} onStepClick={goto} />
      </div>

      <SplitView aside={summary} asideWidth="320px">
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 640 }}>
          {step === 0 && (
            <Card flat>
              <CardHeader>
                <CardTitle>Monitor type</CardTitle>
                <CardDescription>What do you want to check?</CardDescription>
              </CardHeader>
              <CardBody>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--vn-space-3)' }}>
                  {types.map((t) => {
                    const active = selected?.type === t.type;
                    return (
                      <button
                        key={t.type}
                        type="button"
                        onClick={() => pickType(t)}
                        style={{
                          textAlign: 'left',
                          cursor: 'pointer',
                          font: 'inherit',
                          color: 'var(--vn-fg-default)',
                          border: `1px solid ${active ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
                          outline: active ? '1px solid var(--vn-primary-solid)' : 'none',
                          borderRadius: 'var(--vn-radius-lg)',
                          background: active ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
                          padding: 'var(--vn-space-3)',
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{t.label}</span>
                        <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{t.type}</div>
                      </button>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          )}

          {step === 1 && selected && (
            <Card flat>
              <CardHeader>
                <CardTitle>{selected.label} configuration</CardTitle>
                <CardDescription>Fill the fields for this monitor type.</CardDescription>
              </CardHeader>
              <CardBody>
                <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                  {fields.map((f) => (
                    <Field key={f.key} label={f.label} required={f.required} description={f.help}>
                      {(fp) => renderControl(f, fp)}
                    </Field>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {step === 2 && (
            <Card flat>
              <CardHeader>
                <CardTitle>Ready to create</CardTitle>
                <CardDescription>Confirm the summary on the right, then create.</CardDescription>
              </CardHeader>
              <CardBody>
                <p style={{ margin: 0, color: 'var(--vn-fg-muted)' }}>
                  The monitor starts checking on its schedule immediately. You can pause it anytime.
                </p>
              </CardBody>
            </Card>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
            <Button variant="ghost" onClick={() => (step === 0 ? navigate(listPath) : goto(step - 1))}>
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            {step < 2 ? (
              <Button onClick={next} disabled={step === 0 && !selected}>
                Continue
              </Button>
            ) : (
              <Button onClick={submit} disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create monitor'}
              </Button>
            )}
          </div>
        </div>
      </SplitView>
    </Container>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-3)', fontSize: 'var(--vn-font-size-sm)' }}>
      <dt style={{ color: 'var(--vn-fg-muted)' }}>{label}</dt>
      <dd style={{ margin: 0, textAlign: 'right', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</dd>
    </div>
  );
}

